// ============================================================
// shared/pricing.js — LE calcul de prix. Source de vérité métier.
// ------------------------------------------------------------
// Importé par l'API *et* par le front. Une seule implémentation,
// jamais deux : c'est la correction n°1 apportée à AGC, où la
// formule vivait en double (backend + data.jsx) et avait divergé.
//
// 100 % déterministe, aucun LLM, aucune dépendance : ni `pg`,
// ni Express, ni React, ni `process.env`. Se teste sans base.
//
// Entrées au format BASE DE DONNÉES (champs plats). Le driver `pg`
// renvoie les colonnes NUMERIC en CHAÎNES : tout passe par num().
// ============================================================

/**
 * Version de la formule. À incrémenter à CHAQUE changement de calcul.
 * Elle est persistée dans `pieces.pricing_snapshot`, ce qui rend un
 * devis rejouable même après une évolution de la formule.
 *
 * v1 : formule Mertens initiale — gamme d'opérations, marge explicite,
 *      minimum de facturation, arrondi commercial sur le prix unitaire,
 *      sous-traitance en 3 modes, format d'achat matière.
 * v2 : approvisionnement en BARRES ENTIÈRES (arbitrage Charles du
 *      31/07/2026). Quand un format d'achat est renseigné, le lot paie
 *      le nombre de barres réellement achetées, chute comprise, au lieu
 *      de la seule longueur consommée. La masse des pièces reste
 *      distincte de la masse achetée : les traitements au kilo se
 *      paient sur les pièces, pas sur la chute.
 */
// v3 — 4 août 2026. Une seule ligne de coût travail (cycle × taux
// machine, réglage + programmation × taux humain, par lot), traitements
// internes chiffrés en temps et non en achat, taux globaux au lieu d'un
// taux par machine. Voir CLAUDE.md §2bis.
// v5 — 21 août 2026. LE COEFFICIENT DE SOUS-TRAITANCE NE S'APPLIQUE
// PLUS QU'UNE FOIS. Il passait deux fois : une fois par ligne
// (`purchase_coef`), une fois sur la somme (`piece.coefficient_traitement`).
// Mesuré : un zingage à 100 € partait sur le devis à **138 €**
// (100 × 1,20 × 1,15). Charles l'a signalé autrement — « le coefficient
// traitement n'a rien à faire dans la partie matière, chaque traitement
// doit avoir son coefficient » — et il avait raison sur le fond, pas
// seulement sur la place du champ.
//
// Le coefficient vit désormais SUR LA LIGNE, et nulle part ailleurs.
// À défaut, celui des paramètres (`coefficient_sous_traitance`).
// v7 — 26 août 2026. RETOUR AU COMPORTEMENT v5 : `× (quantité − 1)`
// pour TOUTE la production. La v6 avait excepté les OP sans ligne
// prototype ; Charles l'a annulée le jour même — la règle a été
// expressément demandée par le client, et corriger le calcul pour
// rattraper une gamme mal appariée aurait rendu la règle inexplicable.
//
// Le numéro AVANCE au lieu de revenir à 5 : trois pièces portent déjà
// un instantané v6, et deux comportements sous un même numéro
// rendraient un devis irrejouable.
export const PRICING_FORMULA_VERSION = 7;

// La cascade matière du §2.1 vit à part : elle est réutilisée par le
// service qui alimente le référentiel, et elle se teste seule.
import { resoudreMatiere } from './matiere.js';
import { tempsDeLaPiece, multiplicateurs } from './gamme.js';

/** Règles d'arrondi commercial autorisées (miroir du CHECK en base). */
export const ROUNDING_RULES = ['none', 'up_0_50', 'up_1', 'up_5', 'nearest_0_50', 'nearest_1'];

/**
 * LA RÈGLE D'ARRONDI EN CLAIR — et il n'y a qu'un endroit où elle l'est.
 *
 * `up_0_50` s'affichait tel quel dans la synthèse du prix, à côté d'un
 * montant : « Arrondi · up_0_50 · + 11,83 € ». C'est une valeur
 * d'énumération, pas une phrase — la même famille que le libellé de
 * catégorie manquant, où l'écran rend le mot de la base faute d'en
 * connaître un autre.
 *
 * L'écran de saisie, lui, en avait une traduction — dans son propre
 * fichier. Une seule table, et elle vit avec les règles qu'elle nomme.
 */
export const ROUNDING_LABELS = {
  none: 'Aucun',
  up_0_50: 'Au 0,50 € supérieur',
  up_1: "À l'euro supérieur",
  up_5: 'Aux 5 € supérieurs',
  nearest_0_50: 'Au 0,50 € le plus proche',
  nearest_1: "À l'euro le plus proche",
};

/** Modes de tarification d'une sous-traitance (miroir du CHECK en base). */
/**
 * Les modes de facturation, communs à l'interne et au sous-traité.
 *
 * `per_time` n'existe que pour un traitement INTERNE : il compte un
 * temps × un taux, et le taux applicable (machine ou humain) est porté
 * par le traitement lui-même. Un premier arbitrage avait posé
 * « interne = temps × taux machine » ; c'était faux. La tribofinition
 * tourne des heures sans personne — à 70 €/h elle produirait 280 € sur
 * une pièce qui en vaut trente — alors que le microbillage est un
 * opérateur debout devant une cabine.
 */
export const SUBCONTRACTING_MODES = ['flat', 'per_piece', 'per_kg', 'per_time'];

/** Formes de brut (miroir du CHECK en base). */
/**
 * Les QUATRE formes de brut. Un seul vocabulaire, partagé avec
 * `shared/brut.js` et avec la colonne `pieces.raw_shape`.
 *
 * « a_la_cote » est la quatrième, ajoutée au RDV du 5 août : le patron
 * l'a citée deux fois — « tu vas recevoir une plaque ébauchée à 5 mm »,
 * « de l'étiré 40×10 ». C'est le cas où une surépaisseur forfaitaire est
 * le plus fausse, et il est courant en sous-traitance. Sa surépaisseur
 * est NULLE sur les faces déjà bonnes.
 */
export const RAW_SHAPES = ['barreau', 'barre_ronde', 'barre_ronde_prismatique', 'a_la_cote'];

/** L'enveloppe géométrique correspondante, pour `shared/geometry.js`. */
export const FORME_VERS_ENVELOPPE = {
  barreau: 'box',
  barre_ronde: 'cylinder',
  barre_ronde_prismatique: 'cylinder',
  a_la_cote: 'box',
};

// ── Utilitaires numériques ──────────────────────────────────

/**
 * Conversion robuste vers un nombre fini. Gère les chaînes du driver pg,
 * `null`, `undefined`, `NaN`. Renvoie `fallback` si la valeur n'est pas
 * exploitable — donc JAMAIS utilisée seule pour décider qu'une entrée
 * est présente : voir `isProvided()`.
 */
export function num(value, fallback = 0) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Une entrée est-elle réellement renseignée ?
 * Attention : `+null === 0` et `+'' === 0`, tous deux finis. Un simple
 * `Number.isFinite(Number(v))` laisserait donc passer une valeur absente,
 * c'est-à-dire exactement le bug AGC (une marge nulle silencieuse).
 */
export function isProvided(value) {
  if (value === null || value === undefined || value === '') return false;
  return Number.isFinite(typeof value === 'number' ? value : parseFloat(value));
}

/** Arrondi monétaire au centime. */
export function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Arrondi à 3 décimales, pour les masses et les volumes affichés. */
function round3(n) {
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}

/**
 * Arrondi commercial. S'applique AU PRIX UNITAIRE, UNE SEULE FOIS.
 * Arrondir le lot puis diviser puis ré-arrondir détruirait l'arrondi
 * qu'on vient de poser et casserait `total = unitaire × qté`.
 */
/**
 * Assemble les DÉFAUTS de chiffrage depuis `app_settings`.
 *
 * ── Pourquoi cette fonction existe ──
 *
 * Les réglages sont rangés par GROUPE en base : `pricing` porte les
 * marges et les arrondis, `rates` porte les deux taux. Le calcul, lui,
 * attend un seul objet plat.
 *
 * Cet assemblage se faisait à DEUX endroits : `pricingDefaults()` côté
 * serveur, qui lisait les deux groupes, et une ligne du front qui n'en
 * lisait qu'un — `settings.pricing`. Le front passait donc à
 * `computePiece` un objet sans `machine_rate`, sans `human_rate` et sans
 * `rates_confirmed`.
 *
 * Conséquence exacte, mesurée : sur la même pièce, le serveur calculait
 * **214,50 €** et l'écran affichait « Cycle 18,5 min au taux machine
 * (0 €/h) = 0,00 € » plus « Taux machine absent dans Paramètres → Prix
 * et marges » — sur un écran où ils étaient présents et confirmés.
 *
 * C'est le défaut que `shared/` existe pour empêcher : **une seule
 * implémentation, importée des deux côtés.** Il avait été évité sur la
 * formule de prix et reproduit sur ses ENTRÉES.
 *
 * @param {object} settings  le contenu de `app_settings`, par groupe
 */
export function pricingDefaultsFrom(settings = {}) {
  return {
    ...(settings.pricing ?? {}),
    // Les DEUX taux, et deux seulement. Aucune machine n'en porte.
    machine_rate: settings.rates?.machine_rate ?? null,
    human_rate: settings.rates?.human_rate ?? null,
    // Tant qu'ils ne sont pas confirmés, chaque pièce le signale : un
    // prix bâti sur un taux non confirmé n'est pas un prix.
    rates_confirmed: settings.rates?.rates_confirmed === true,
  };
}

export function roundCommercial(value, rule) {
  const v = num(value);
  switch (rule) {
    case 'up_0_50':
      return round2(Math.ceil(round2(v * 2)) / 2);
    case 'up_1':
      return Math.ceil(round2(v));
    case 'up_5':
      return Math.ceil(round2(v) / 5) * 5;
    case 'nearest_0_50':
      return round2(Math.round(round2(v) * 2) / 2);
    case 'nearest_1':
      return Math.round(round2(v));
    case 'none':
    default:
      return round2(v);
  }
}

// ── Géométrie du brut (volume seul ; le dimensionnement est dans geometry.js) ──

/**
 * Volume du brut en cm³, à partir des cotes stockées sur la pièce.
 * box      : V = L × l × h      (mm) → /1000 = cm³
 * cylinder : V = π × (Ø/2)² × L (mm) → /1000 = cm³
 * `cylinder_box` (pièce prismatique débitée dans une barre ronde)
 * partage la formule du cylindre : c'est bien du rond qu'on achète.
 */
export function rawVolumeCm3(piece) {
  if (FORME_VERS_ENVELOPPE[piece.raw_shape] === 'cylinder') {
    const diameter = num(piece.raw_diameter);
    const length = num(piece.raw_length);
    return (Math.PI * (diameter / 2) ** 2 * length) / 1000;
  }
  return (num(piece.raw_l) * num(piece.raw_w) * num(piece.raw_h)) / 1000;
}

/**
 * Section d'un format d'achat, en mm². Renvoie `null` si le format n'est
 * pas un profilé débitable (plaque, format inconnu) : dans ce cas on
 * facture le volume théorique du brut, sans saignée ni chute.
 */
function formatSectionMm2(format) {
  const d = format?.dimensions ?? {};
  switch (format?.kind) {
    case 'bar_round':
      return Math.PI * (num(d.diameter) / 2) ** 2;
    case 'bar_square': {
      const side = isProvided(d.side) ? num(d.side) : num(d.width);
      const other = isProvided(d.height) ? num(d.height) : side;
      return side * other;
    }
    case 'tube': {
      const outer = num(d.diameter);
      const thickness = num(d.thickness);
      const inner = Math.max(0, outer - 2 * thickness);
      return Math.PI * ((outer / 2) ** 2 - (inner / 2) ** 2);
    }
    default:
      return null;
  }
}

/**
 * Approvisionnement en barres : on facture LA BARRE ENTIÈRE.
 *
 * Règle tranchée par Charles le 31 juillet 2026. Quand un format
 * d'achat est renseigné, le lot coûte le nombre de barres qu'il faut
 * réellement acheter — pas la longueur consommée. La chute de fin de
 * barre est payée par le devis qui la crée, parce que c'est lui qui
 * la crée : le reliquat n'est pas garanti réutilisable, et l'atelier
 * l'a effectivement sortie de son stock.
 *
 * Conséquence à connaître : le coût matière est en escalier. Passer de
 * 29 à 30 pièces sur une barre de 29 fait acheter une seconde barre et
 * double le poste matière. C'est voulu et c'est le réel — et c'est
 * précisément ce que les paliers de quantité servent à montrer au
 * client.
 *
 * @returns {{ piecesPerBar: number, barsNeeded: number, dropPerBarMm: number,
 *             purchasedLengthMm: number, usedLengthMm: number } | null}
 */
function barSupply(cutLengthMm, qty, format) {
  const stock = num(format?.stock_length);
  const kerf = num(format?.kerf_mm);
  if (!(cutLengthMm > 0) || !(stock > 0)) return null;
  const withKerf = cutLengthMm + kerf;
  const piecesPerBar = Math.floor(stock / withKerf);
  if (piecesPerBar < 1) return null; // pièce plus longue que la barre : cas à traiter à la main
  const barsNeeded = Math.ceil(qty / piecesPerBar);
  return {
    piecesPerBar,
    barsNeeded,
    dropPerBarMm: stock - piecesPerBar * withKerf,
    // Ce qui est ACHETÉ et facturé au client.
    purchasedLengthMm: barsNeeded * stock,
    // Ce qui part réellement à l'usinage : sert aux traitements au kilo,
    // qui se paient sur le poids des pièces, pas sur celui de la chute.
    usedLengthMm: qty * withKerf,
  };
}

// ── Le calcul ───────────────────────────────────────────────

/**
 * @typedef {Object} PricingWarning
 * @property {string} field   chemin du champ fautif (ex. `subcontracting[1].temps_min`)
 * @property {'error'|'warning'} level  `error` bloque le PDF et l'envoi
 * @property {string} message  phrase affichable telle quelle
 */

/**
 * Calcule le prix d'une pièce.
 *
 * RÈGLE ABSOLUE : une entrée manquante ne vaut jamais zéro. Elle produit un
 * `warnings[]` de niveau `error` et la pièce est marquée incomplète. Une pièce
 * incomplète ne peut ni être imprimée en PDF ni être envoyée. Chez AGC, une
 * pièce sans coefficient perdait 12 % de marge en silence et un devis à 0 €
 * pouvait partir chez le client.
 *
 * @param {Object} piece  pièce au format base (champs plats)
 * @param {Object} [options]
 * @param {Object} [options.defaults]  valeurs par défaut issues de `app_settings.pricing`
 * @param {number} [options.qty]       quantité forcée — utilisé pour les paliers
 * @param {string} [options.computedAt] horodatage ISO injecté (garde la fonction testable)
 */
export function computePiece(piece = {}, options = {}) {
  const defaults = options.defaults ?? {};
  const warnings = [];
  const warn = (field, level, message) => warnings.push({ field, level, message });

  // ── Quantité ──
  const qtyRaw = options.qty ?? piece.qty;
  if (!isProvided(qtyRaw) || num(qtyRaw) < 1) {
    warn('qty', 'error', 'Quantité manquante ou invalide.');
  }
  const qty = Math.max(1, Math.round(num(qtyRaw, 1)));

  // ══════════════════════════════════════════════════════════════
  // IL N'Y A PLUS DE MARGE — décision du RDV du 5 août 2026.
  // ══════════════════════════════════════════════════════════════
  //
  // Le taux horaire de Mertens est un TAUX DE VENTE : la marge est
  // déjà dedans. L'appliquer par-dessus la comptait DEUX FOIS, et
  // surtout personne ne savait plus si 70 €/h était un coût ou un prix.
  //
  // `temps × taux` donne donc directement le PRIX DE VENTE de
  // l'usinage. Il n'y a plus de coût puis de prix, plus de « coût de
  // revient » comme étape intermédiaire côté usinage.
  //
  // Ce qui reste, ce sont trois coefficients de REVENTE, un par poste
  // que Mertens achète et refacture — matière, sous-traitance,
  // transport. Ils ne s'appliquent PAS à l'usinage.
  // ── LA PIÈCE PREND LE PAS SUR LE PARAMÈTRE GLOBAL ──
  //
  // Demande de Charles, 11 août 2026 : la marge de revente se décide par
  // affaire, pas une fois pour toutes dans un réglage que personne ne
  // relit. Vide, on garde la valeur des paramètres ; rempli, c'est le
  // sien qui gagne — c'est la même règle que pour le prix d'achat
  // matière et le transport, et elle est écrite en clair à l'écran.
  //
  // `isProvided` et non `||` : un coefficient écrit à 1 est une DÉCISION
  // — « pas de marge sur cette affaire » — et il ne doit pas retomber
  // sur le paramètre global. C'est l'invariant 5 appliqué à un
  // coefficient : un 1 décidé n'est pas un 1 par défaut.
  const coefMatiere = isProvided(piece.coefficient_matiere)
    ? num(piece.coefficient_matiere, 1)
    : isProvided(defaults.coefficient_matiere)
      ? num(defaults.coefficient_matiere, 1) : 1;
  const coefMatiereSource = isProvided(piece.coefficient_matiere) ? 'piece' : 'parametres';

  const coefSousTraitance = isProvided(piece.coefficient_traitement)
    ? num(piece.coefficient_traitement, 1)
    : isProvided(defaults.coefficient_sous_traitance)
      ? num(defaults.coefficient_sous_traitance, 1) : 1;
  const coefTraitementSource = isProvided(piece.coefficient_traitement) ? 'piece' : 'parametres';
  const coefTransport = isProvided(defaults.coefficient_transport)
    ? num(defaults.coefficient_transport, 1) : 1;

  // ── Règle d'arrondi ──
  let rounding = piece.rounding_rule ?? defaults.default_rounding;
  if (!ROUNDING_RULES.includes(rounding)) {
    if (rounding != null && rounding !== '') {
      warn('rounding_rule', 'error', `Règle d'arrondi inconnue : ${rounding}.`);
    } else {
      warn('rounding_rule', 'warning', "Aucune règle d'arrondi : le prix n'est pas arrondi.");
    }
    rounding = 'none';
  }

  // ── Minimum de facturation ──
  const minInvoiceRaw = isProvided(piece.min_invoice)
    ? piece.min_invoice
    : defaults.default_min_invoice;
  const minInvoice = Math.max(0, num(minInvoiceRaw));

  // ── Coefficient d'achat ──
  const purchaseCoefRaw = isProvided(piece.purchase_coef)
    ? piece.purchase_coef
    : defaults.default_purchase_coef;

  const isPurchase = piece.is_purchase === true;

  // ══════════════════════════════════════════════════════════════
  // ── MATIÈRE ──────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════
  //
  // ── Pourquoi plus rien ici ne BLOQUE un prix ──
  //
  // Ces trois contrôles étaient en `error`, donc ils rendaient la pièce
  // incomplète, donc ils supprimaient le prix. C'était juste tant que la
  // matière entrait dans le prix pièce. Depuis le 5 août, **elle n'y est
  // plus** : l'usinage est un prix de vente à lui seul, et la matière
  // sort en ligne séparée, hors engagement (§4).
  //
  // Un prix d'usinage parfaitement calculé n'a donc plus aucune raison
  // de disparaître parce qu'on ignore le cours de l'inox. Ce sont des
  // `warning` : ils remontent à l'écran, ils descendent la confiance, ils
  // ne suppriment rien.
  //
  // ── Et la valeur, elle, ne manque presque jamais ──
  //
  // `shared/matiere.js` déroule la cascade du §2.1 — saisie, offre
  // fournisseur, référentiel, web, défaut par famille. Le dernier
  // recours donne 7,85 g/cm³ et 2,50 €/kg pour un acier : grossier,
  // marqué `estime`, affiché en gris, écrasable en un clic — et
  // infiniment plus utile qu'un refus.
  const material = { cost_total: 0 };

  // ── §1.4 — « matière fournie par le client » ──
  // Coût nul, et surtout : la pièce n'est PAS incomplète pour autant.
  // C'est le seul endroit du calcul où zéro est une vraie valeur et non
  // une absence — parce qu'ici quelqu'un l'a explicitement décidé.
  const matiereFournie = piece.matiere_fournie_client === true;

  if (!isPurchase && matiereFournie) {
    material.fournie_par_client = true;
    material.cost_total = 0;
    material.cost_unit = 0;
  } else if (!isPurchase) {
    // La cascade. `piece.material_ref` est la ligne du référentiel,
    // `piece.offre_matiere` la dernière offre reçue pour cette nuance ;
    // l'une et l'autre peuvent manquer sans rien empêcher.
    const cascade = resoudreMatiere(
      {
        densite_saisie: piece.density,
        prix_kg_saisi: piece.prix_kg_retenu ?? piece.price_per_kg,
        referentiel: piece.material_ref ?? null,
        offre: piece.offre_matiere ?? null,
        famille: piece.material_famille ?? piece.material_ref?.category ?? null,
        // ── La DÉSIGNATION, et c'est elle qui arme le garde-fou de bande ──
        //
        // Sans elle, `familleDepuisDesignation` n'est jamais consulté :
        // une matière dont la `category` est vide au référentiel n'a
        // plus de famille, donc plus de bande, donc 7,3 g/cm³ sur de
        // l'aluminium passe sans un mot. C'est la surface qu'on n'avait
        // pas nommée — le garde-fou existait, on ne lui donnait pas de
        // quoi juger.
        designation: piece.material_ref?.designation ?? piece.material_label ?? null,
      },
      options.computedAt ?? undefined,
    );

    if (cascade.densite.valeur == null) {
      warn('density', 'warning',
        'Densité inconnue et matière non rattachée à une famille : la ligne matière reste à confirmer.');
    } else if (cascade.densite.source === 'estime') {
      warn('density', 'info', `Densité estimée par famille. ${cascade.densite.note ?? ''}`.trim());
    }
    if (cascade.prix_kg.valeur == null) {
      warn('price_per_kg', 'warning',
        'Prix au kilo inconnu et matière non rattachée à une famille : la ligne matière reste à confirmer.');
    } else if (cascade.prix_kg.source === 'estime' || cascade.prix_kg.source === 'web') {
      warn('price_per_kg', 'info', cascade.prix_kg.note ?? 'Prix au kilo à confirmer.');
    }
    if (!RAW_SHAPES.includes(piece.raw_shape)) {
      warn('raw_shape', 'warning',
        'Forme de brut absente ou inconnue : la masse matière ne peut pas être calculée.');
    }

    // La provenance voyage jusqu'à l'écran — c'est elle qui allume la
    // pastille verte, orange, grise ou bleue du §2.5.
    material.density_source = cascade.densite.source;
    material.density_confiance = cascade.densite.confiance;
    material.density_note = cascade.densite.note;
    material.price_source = cascade.prix_kg.source;
    material.price_confiance = cascade.prix_kg.confiance;
    material.price_note = cascade.prix_kg.note;
    // Ce qui a été REMPLACÉ reste lisible (§1.3 : « l'écran affiche la
    // valeur retenue ET la valeur remplacée, avec la mention de qui
    // gagne »).
    material.price_remplace = cascade.prix_kg.remplace;
    material.density_remplace = cascade.densite.remplace;
    // Les densités ÉCARTÉES par le garde-fou, avec leur raison. C'est ce
    // que le serveur compte comme repli (invariant 24) : un chemin qui
    // retombe se voit, il ne se déduit pas.
    material.density_refuses = cascade.densite.refuses ?? [];

    const density = num(cascade.densite.valeur);
    const format = piece.raw_format ?? null;
    const section = format ? formatSectionMm2(format) : null;
    // Un format d'achat porte son propre prix au kilo : c'est le prix de
    // CE format chez CE fournisseur, il prime sur la cascade générale.
    const pricePerKg = isProvided(format?.price_per_kg)
      ? num(format.price_per_kg)
      : num(cascade.prix_kg.valeur);

    // Longueur débitée : la cote portée par l'axe de la barre.
    const cutLength =
      FORME_VERS_ENVELOPPE[piece.raw_shape] === 'cylinder'
        ? num(piece.raw_length)
        : num(piece.raw_h);

    // Deux masses, et il ne faut surtout pas les confondre :
    //  - la masse ACHETÉE, qui est facturée au client (barres entières) ;
    //  - la masse des PIÈCES, sur laquelle se calculent les traitements
    //    au kilo. Faire payer un zingage sur le poids de la chute serait
    //    une erreur, et elle passerait inaperçue.
    let purchasedVolumeCm3;
    let pieceVolumeCm3;
    let bar = null;

    if (section != null) {
      bar = barSupply(cutLength, qty, format);
      if (bar) {
        purchasedVolumeCm3 = (section * bar.purchasedLengthMm) / 1000;
        pieceVolumeCm3 = (section * bar.usedLengthMm) / 1000;
      } else {
        warn(
          'raw_format',
          'warning',
          "Format d'achat inexploitable pour cette cote : facturation au volume théorique.",
        );
        pieceVolumeCm3 = rawVolumeCm3(piece) * qty;
        purchasedVolumeCm3 = pieceVolumeCm3;
      }
    } else {
      pieceVolumeCm3 = rawVolumeCm3(piece) * qty;
      purchasedVolumeCm3 = pieceVolumeCm3;
    }

    if (!(purchasedVolumeCm3 > 0)) {
      // Même raison que plus haut : cette absence n'ampute que la ligne
      // matière, qui est hors du prix d'usinage. Elle se signale, elle
      // ne supprime pas un prix par ailleurs juste.
      warn('raw_dimensions', 'warning', 'Cotes du brut absentes : la masse matière est incalculable.');
    }

    // ══════════════════════════════════════════════════════════════
    // ── UN ZÉRO CONVERTI EST UNE ABSENCE DÉGUISÉE (invariant 5) ──
    // ══════════════════════════════════════════════════════════════
    //
    // `density` sort de `num(cascade.densite.valeur)`, et `num(null)`
    // vaut 0. Trois lignes plus bas, la masse valait 0 kg et le coût
    // matière 0,00 € — sur une pièce dont personne ne connaît ni la
    // densité ni les cotes.
    //
    // C'est le motif exact de l'invariant 12 : **une fonction de
    // conversion est un lieu de naissance.** Et le résultat se lisait à
    // l'écran, à côté d'une masse finie qui, elle, disait honnêtement
    // « — » : trois cases côte à côte, deux mentent, une dit vrai, et
    // rien ne les distingue.
    //
    // On nomme donc les deux inconnues AVANT de multiplier. Une masse
    // n'existe que si la densité ET le volume existent.
    const densiteConnue = cascade.densite.valeur != null && density > 0;
    const volumeConnu = purchasedVolumeCm3 > 0 && pieceVolumeCm3 > 0;
    const masseCalculable = densiteConnue && volumeConnu;
    material.masse_inconnue = !masseCalculable;
    material.masse_inconnue_raison = masseCalculable
      ? null
      : !densiteConnue && !volumeConnu
        // En minuscule : ces phrases sont des FRAGMENTS, affichés après
        // un « — » ou dans une colonne de calcul. Une majuscule au
        // milieu d'une phrase se lit comme un recollage.
        ? 'ni la densité ni les cotes du brut ne sont connues'
        : !densiteConnue
          ? 'la densité n’est pas connue'
          : 'les cotes du brut ne sont pas connues';

    const purchasedMassKg = masseCalculable ? (purchasedVolumeCm3 / 1000) * density : null;
    const piecesMassKg = masseCalculable ? (pieceVolumeCm3 / 1000) * density : null;

    material.volume_cm3 = volumeConnu ? round3(pieceVolumeCm3 / qty) : null;
    material.mass_per_piece_kg = masseCalculable ? round3(piecesMassKg / qty) : null;
    /**
     * La masse de la pièce FINIE — celle que le client reçoit.
     *
     * Elle manquait, et son absence se voyait à l'écran : « masse par
     * pièce 0,16 kg » à côté de « masse achetée 0,16 kg », deux nombres
     * identiques sur une pièce d'où l'on sort 35 % de copeaux. Ce qui
     * était affiché comme la masse d'une pièce était celle de son brut.
     *
     * Elle ne remplace PAS `mass_total_kg` dans le calcul d'un
     * traitement au kilo : cette règle-là a été tranchée le 31 juillet
     * et se paie sur la masse débitée. La changer se décide, elle ne se
     * glisse pas dans un correctif d'affichage.
     */
    // `finished_volume` est porté par la pièce, calculé par
    // `shared/geometry.js`. Absent, la masse finie l'est aussi — et
    // c'est un tiret à l'écran, jamais un zéro.
    const volumeFini = Number(piece.finished_volume);
    // La densité entre AUSSI dans cette masse-là. Le contrôle ne portait
    // que sur le volume : avec un volume fini connu et une densité
    // inconnue, `volumeFini/1000 × 0` rendait 0, et l'écran affichait
    // « masse de la pièce finie 0 kg » juste à côté de « masse achetée —
    // » et « coût matière — ». Le zéro converti a survécu dans la SEULE
    // case qui était juste avant, parce qu'on n'y avait regardé qu'une
    // des deux entrées.
    material.mass_finished_kg =
      densiteConnue && Number.isFinite(volumeFini) && volumeFini > 0
        ? round3((volumeFini / 1000) * density)
        : null;
    // Utilisée par la sous-traitance au kilo : le poids des pièces.
    material.mass_total_kg = masseCalculable ? round3(piecesMassKg) : null;
    // Ce qui est réellement acheté, donc facturé.
    material.purchased_mass_kg = masseCalculable ? round3(purchasedMassKg) : null;
    material.price_per_kg = pricePerKg;
    // La densité RETENUE, en clair. Elle voyageait sous forme de source
    // et de confiance, jamais comme nombre — donc l'écran ne pouvait pas
    // écrire « 108 cm³ × 2,66 g/cm³ = 0,287 kg », et un chiffre dont on
    // ne peut pas montrer le calcul se croit ou ne se croit pas.
    material.density = cascade.densite.valeur;
    // ── Une matière SANS prix n'a pas un coût de zéro ──
    //
    // Sans famille, la cascade rend `null` — elle refuse d'inventer, et
    // c'est juste. Mais `num(null)` vaut 0, donc le coût matière tombait
    // à 0,00 € et la ligne de l'offre affichait « Matière — 0,00 € ».
    //
    // C'est le canal qui ment : zéro est un nombre valide, il
    // s'additionne, il s'affiche, et il dit « c'est gratuit » là où il
    // faudrait lire « je ne sais pas ». Même famille que les trois temps
    // à zéro de l'agent 2 (§11, ligne 7) et que le `null` du décodeur
    // d'encombrement lu comme une cote absente.
    material.prix_inconnu = !(pricePerKg > 0);
    // Un coût matière n'existe pas davantage que la masse dont il
    // dérive. `round2(null * 10.5)` valait 0 : la ligne « Matière » de
    // l'OFFRE affichait 0,00 € sur une pièce dont rien n'était calculé.
    material.cout_inconnu = material.prix_inconnu || material.masse_inconnue;

    // ── §1.3 — l'ordre de priorité, appliqué tel quel ──
    //
    //   1. montant matière saisi sur la pièce
    //   2. prix au kilo saisi sur la pièce × masse achetée
    //   3. prix au kilo du référentiel (confirmé)
    //   4. prix au kilo trouvé (web / estimé)
    //
    // Les niveaux 2 à 4 sont déjà tranchés par la cascade ci-dessus, qui
    // les a rangés dans le même ordre. Il ne reste à traiter ici que le
    // niveau 1 : un MONTANT, qui court-circuite masse et prix au kilo.
    const montantCalcule = material.cout_inconnu ? null : round2(purchasedMassKg * pricePerKg);
    material.cost_calcule = montantCalcule;

    if (isProvided(piece.montant_matiere_retenu)) {
      material.cost_total = num(piece.montant_matiere_retenu);
      material.montant_retenu = material.cost_total;
      material.price_source = 'manuel';
      material.price_confiance = 'sure';
      // Le montant calculé n'est jamais écrasé : il reste affiché en
      // gris à côté, pour comparaison (§1.1). C'est ce qui permet de
      // voir qu'on vient de retenir 60 € là où le programme disait 47,80.
      material.price_note = montantCalcule == null
        ? 'Montant retenu à la main. Le programme ne sait pas le calculer : '
          + `${material.masse_inconnue_raison ?? 'prix au kilo inconnu'}.`
        : `Montant retenu à la main. Calcul du programme : ${montantCalcule.toFixed(2)} €.`;
    } else {
      material.cost_total = montantCalcule;
      material.montant_retenu = null;
    }
    material.cost_unit = material.cost_total == null ? null : material.cost_total / qty;
    material.from_format = bar
      ? {
          kind: format.kind,
          stock_length: num(format.stock_length),
          kerf_mm: num(format.kerf_mm),
          cut_length_mm: cutLength,
          pieces_per_bar: bar.piecesPerBar,
          bars_needed: bar.barsNeeded,
          drop_per_bar_mm: round3(bar.dropPerBarMm),
          // Ce que le lot laisse comme reliquat, tout compris. C'est
          // le chiffre à regarder avant d'ajuster une quantité.
          unused_length_mm: round3(bar.purchasedLengthMm - bar.usedLengthMm),
        }
      : null;
  }

  // ── Le TRAVAIL : trois nombres, deux taux ──
  //
  // Le modèle précédent demandait une ligne par opération élémentaire,
  // chacune avec sa machine, son cycle, son réglage, sa programmation et
  // ses deux taux. Sur une rondelle simple : quatre lignes et
  // vingt-quatre nombres à remplir, tous vides. Le deviseur faisait le
  // travail du programme.
  //
  // Il en reste TROIS, et c'est le niveau auquel un deviseur a
  // réellement un avis. Personne ne sait dire « le réglage de la prise 2,
  // c'est douze minutes » ; tout le monde sait dire « quarante minutes de
  // réglage en tout ».
  //
  //   cycle          — par PIÈCE  × taux machine
  //   réglage        — par LOT    × taux humain
  //   programmation  — par LOT    × taux humain
  //
  // Le taux humain est le plus élevé : **une minute de réglage coûte
  // plus cher qu'une minute d'usinage**. C'est ce qui fait du nombre de
  // prises le premier levier du prix en petite série. Et c'est aussi ce
  // qui fait chuter le prix unitaire en série : réglage et programmation
  // s'amortissent sur la quantité, le cycle non.
  // ── LA GAMME COMMANDE — §8.1 ──
  //
  //     relevé  →  GAMME  →  temps  →  prix
  //
  // Les trois temps ne sont plus lus sur la pièce : ils sont DÉRIVÉS de
  // la gamme. Corriger la gamme recalcule les temps, donc le prix.
  //
  // Sauf ce que le deviseur a figé à la main (§8.4) : une décision
  // humaine ne s'efface pas toute seule. `tempsDeLaPiece` arbitre, et
  // signale le conflit sans jamais le trancher.
  //
  // Sans gamme, on retombe sur les trois champs de la pièce — le mode
  // d'avant le 5 août, qui reste valable tant que l'agent n'a pas
  // produit de gamme.
  const temps = tempsDeLaPiece(piece, piece.gamme ?? []);
  const cycleMin = num(temps.cycle_time_min);
  const setupMin = num(temps.setup_time_min);
  const programmingMin = num(temps.programming_time_min);
  // Le débit d'une barre entière : la scie tourne UNE fois pour la
  // série. C'est le seul cycle qui ne se multiplie pas, et il n'existe
  // que dans la gamme — aucun champ de la pièce ne le porte.
  const cycleParLot = num(temps.cycle_par_lot);
  // Le CONTRÔLE DE PRODUCTION : par pièce, mais au taux humain — c'est
  // un opérateur qui mesure. Vaut 0 sur toutes les gammes d'avant le
  // 11 août, donc aucun devis déjà chiffré ne bouge.
  // Le contrôle n'a plus de panier : il est fondu dans le cycle et dans
  // la préparation par `tempsDeLaPiece`. Un total qui vit à deux
  // endroits finit par diverger.

  for (const p of temps.derive.problemes) {
    warn('gamme', 'warning', p);
  }
  for (const c of temps.conflits) {
    // Le conflit n'est pas une erreur : les deux valeurs sont légitimes,
    // et c'est à l'humain de trancher. Il ne bloque donc pas le prix —
    // il s'affiche, avec les deux nombres et les deux boutons.
    warn(`temps.${c.champ}`, 'warning', `${c.message} Choisissez laquelle garder.`);
  }

  const machineRate = num(defaults.machine_rate);
  const humanRate = num(defaults.human_rate);

  const hasWork = cycleMin > 0 || setupMin > 0 || programmingMin > 0;
  // ── Le prix ne se BLOQUE plus faute d'un temps VALIDÉ ──
  //
  // « Aucun temps saisi » était une `error`, donc pas de prix, donc le
  // bandeau affichait « Prix bloqué » et le total du devis 0,00 € — sur
  // une pièce dont l'agent avait proposé 36 / 45 / 90 min, affichées
  // deux blocs plus haut.
  //
  // Un temps proposé par l'agent EST une valeur : il porte sa
  // provenance (`estime`) et sa confiance, et il s'écrase en un clic.
  // Refuser de s'en servir, c'était appliquer à l'ESTIMATION la règle
  // qui protège la PERCEPTION.
  //
  // Ce qui reste absent est ce que personne n'a encore produit : ni
  // gamme, ni saisie, ni proposition. Là il n'y a rien à chiffrer — et
  // le message dit le geste qui manque, pas « bloqué ».
  const tempsEstimes = ['cycle', 'montage', 'programmation']
    .filter((c) => temps.sources?.[c] === 'estime');
  if (!isPurchase && !hasWork) {
    warn('temps', 'error',
      'Les temps ne sont pas encore estimés : lancez « Estimer les temps » ou saisissez-les.');
  } else if (tempsEstimes.length) {
    warn('temps', 'info',
      `Prix calculé sur ${tempsEstimes.length === 3 ? 'les temps proposés' : `le temps de ${tempsEstimes.join(' et ')} proposé`} `
      + 'par l’agent, non encore validés. Provenance : estimé.');
  }
  if (cycleMin > 0 && !(machineRate > 0)) {
    warn('machine_rate', 'error', 'Taux machine absent dans Paramètres → Prix et marges.');
  }
  if ((setupMin > 0 || programmingMin > 0) && !(humanRate > 0)) {
    warn('human_rate', 'error', 'Taux humain absent dans Paramètres → Prix et marges.');
  }
  // ── Un taux non confirmé ne produit PAS de prix ────────────────────
  //
  // C'était un simple avertissement, et l'écran affichait « Cycle 0 min
  // au taux machine (0 €/h) = 0,00 € » : un prix de zéro qui ressemble
  // à un vrai prix. C'est exactement le garde-fou « zéro n'est pas une
  // estimation » écrit pour l'agent 2 et jamais appliqué au calcul.
  //
  // Un devis incomplet montre CE QUI MANQUE, jamais un prix faux.
  if (defaults.rates_confirmed === false) {
    warn(
      'rates',
      'error',
      'Taux horaires pas encore confirmés : renseignez-les dans Paramètres → Prix et marges, ' +
        'puis cochez la confirmation. Aucun prix n’est calculable avant.',
    );
  }

  // ── LA PRODUCTION SE COMPTE SUR LES PIÈCES QUI RESTENT ──
  //
  // Le prototype contient le montage, la programmation ET l'usinage du
  // premier article : la production ne porte donc que sur `qté − 1`.
  // À une pièce, elle vaut zéro — et c'est juste, puisque le prototype
  // A PRODUIT la pièce.
  //
  // `tempsDeLaPiece` décide du multiplicateur, parce que c'est lui qui
  // sait quel modèle la gamme emploie : une gamme d'avant le 11 août
  // garde `× quantité`, sinon un devis déjà remis changerait de prix.
  // Calculé ICI avec la quantité RÉELLEMENT chiffrée : un palier à 50
  // pièces doit se compter sur 49, pas sur la quantité de référence de
  // la pièce.
  const multProduction = temps.modele_op ? Math.max(0, qty - 1) : qty;

  // ── LA RÈGLE NE SE DISCUTE PAS : `× (quantité − 1)`, POUR TOUTE
  //    LA PRODUCTION — v7, 26 août 2026 ──
  //
  // La v6 avait introduit une exception : une production dont l'OP ne
  // porte pas de ligne prototype se comptait sur la quantité PLEINE,
  // parce que sa première pièce n'était comptée nulle part. Le
  // raisonnement tient arithmétiquement, et il a été ANNULÉ.
  //
  // Charles : « la production se compte sur quantité − 1, cette règle a
  // été expressément demandée par le client. Tant pis si on n'a pas les
  // valeurs de ChatGPT. Reviens en arrière. »
  //
  // Il a raison sur le fond, et la v6 traitait le SYMPTÔME. Le vrai
  // défaut n'est pas le multiplicateur : c'est une gamme dont les OP ne
  // sont pas appariées. Une OP porte DEUX lignes — prototype et
  // production — et quand c'est le cas la règle est exacte. Corriger le
  // calcul pour rattraper une gamme mal formée aurait rendu la règle
  // impossible à expliquer, et une règle qu'on n'explique pas ne se
  // défend pas devant un client.
  //
  // Le sous-facturation existe donc toujours sur une gamme non
  // appariée, et elle est SIGNALÉE — `ops_sans_montage` la nomme
  // depuis le 11 août. La correction est en amont : faire produire des
  // paires. Voir `docs/plancorrection.md`, chantier J2.
  const cycleCost = ((cycleMin * multProduction + cycleParLot) / 60) * machineRate;
  const humanCost = ((setupMin + programmingMin) / 60) * humanRate;

  const work = {
    cycle_time_min: cycleMin,
    cycle_par_lot_min: cycleParLot,
    // Sur combien de pièces la production est comptée. Affiché en clair
    // à l'écran : « × 0 pièce — la première est dans le prototype ».
    production_multiplicateur: multProduction,

    // Les trois totaux du 11 août, tels quels pour l'affichage. Ils
    // valent 0 sur une gamme d'avant, et c'est ainsi qu'on distingue les
    // deux modèles sans drapeau supplémentaire.
    totaux_gamme: {
      prototype: temps.derive.prototype_lot,
      production_piece: round2(
        num(temps.derive.production_piece) + num(temps.derive.production_piece_seule),
      ),
      production_lot: temps.derive.production_lot,
      controle_lot: temps.derive.controle_lot,
      controle_piece: temps.derive.controle_piece,
    },
    setup_time_min: setupMin,
    programming_time_min: programmingMin,
    // D'où vient chaque temps : `gamme`, `saisi` (figé à la main), ou
    // `saisi_sans_gamme`. C'est ce qui allume la mention « saisi à la
    // main » à l'écran — sans elle, un total figé et un total dérivé
    // s'affichent à l'identique.
    sources: temps.sources,
    conflits: temps.conflits,
    programmation_evitee_min: temps.derive.programmation_evitee,
    programmation_deja_faite: temps.derive.programmation_deja_faite,
    // Les trois multiplicateurs du §8.3. À quantité 50 la colonne
    // devient « × 50 / × 1 / × 1 » et les deux dernières lignes ne
    // bougent pas d'une minute.
    multiplicateurs: multiplicateurs(qty),
    machine_rate: machineRate,
    human_rate: humanRate,
    // Le cycle est multiplié par la quantité, le reste non : c'est toute
    // la mécanique de l'amortissement, et elle doit se lire ici.
    // Les minutes AFFICHÉES sont celles qu'on FACTURE. Un seul
    // multiplicateur, donc une addition qui tombe juste à l'écran.
    machine_minutes: round3(cycleMin * multProduction + cycleParLot),
    human_minutes: round3(setupMin + programmingMin),
    cycle_cost: round2(cycleCost),
    human_cost: round2(humanCost),
    cost_total: round2(cycleCost + humanCost),
  };
  const workCost = cycleCost + humanCost;

  // ── Sous-traitance ──
  const subs = Array.isArray(piece.subcontracting) ? piece.subcontracting : [];
  const massTotalKg = num(material.mass_total_kg);
  const subcontracting = subs.map((sc, i) => {
    // ── Un traitement INTERNE n'est pas un achat ──
    //
    // Microbillage, sablage, tribofinition, rectification, soudure,
    // frettage : Mertens les fait. Ça coûte du TEMPS × TAUX MACHINE,
    // exactement comme un cycle — le traitement occupe une machine du
    // parc. Le router en achat de service produisait un prix faux dans
    // les deux sens : une pièce éternellement incomplète faute d'un prix
    // que personne n'a, et — s'il avait été saisi — une marge d'achat
    // appliquée à du temps d'atelier.
    if (sc.nature === 'interne') {
      // Un traitement interne a les MÊMES modes que la sous-traitance,
      // plus `per_time`. Ce qui change, c'est qu'il ne porte pas la
      // marge d'achat : c'est du travail de la maison.
      const mode = SUBCONTRACTING_MODES.includes(sc.mode) ? sc.mode : 'flat';
      let cost = 0;
      let base = null;

      if (mode === 'per_time') {
        const minutes = num(sc.temps_min);
        // Le taux vient du TRAITEMENT, pas d'une règle globale : lui
        // seul sait s'il occupe une machine ou un homme.
        const taux = sc.taux_type === 'humain' ? humanRate : machineRate;
        if (!(minutes > 0)) {
          warn(`subcontracting[${i}].temps_min`, 'error',
            `Traitement interne au temps sans durée (${sc.label ?? i + 1}).`);
        }
        if (!sc.taux_type) {
          warn(`subcontracting[${i}].taux_type`, 'error',
            `Traitement interne au temps sans taux applicable (${sc.label ?? i + 1}) : machine ou humain ?`);
        }
        if (minutes > 0 && !(taux > 0)) {
          warn(`subcontracting[${i}].taux_type`, 'error',
            `Taux ${sc.taux_type ?? '?'} absent : « ${sc.label ?? i + 1} » n'est pas chiffrable.`);
        }
        cost = ((minutes * qty) / 60) * taux;
      } else {
        const unitPrice = num(sc.unit_price);
        if (!isProvided(sc.unit_price)) {
          warn(`subcontracting[${i}].unit_price`, 'warning',
            `Coût de traitement interne à confirmer (${sc.label ?? i + 1}) : non compté dans le total.`);
        }
        if (mode === 'per_kg' && !(num(material.mass_total_kg) > 0)) {
          warn(`subcontracting[${i}].mode`, 'error',
            `Traitement au kilo sans masse calculable (${sc.label ?? i + 1}).`);
        }
        base = mode === 'flat'
          ? unitPrice
          : mode === 'per_piece'
            ? unitPrice * qty
            : unitPrice * num(material.mass_total_kg);
        cost = Math.max(base, num(sc.min_invoice));
      }

      return {
        label: sc.label ?? null,
        treatment_id: sc.treatment_id ?? null,
        // `null` et pas 0 : un coût inconnu n'est pas un coût nul, et
        // c'est ce drapeau qui fait écrire « à confirmer » à l'écran
        // plutôt que « 0,00 € ».
        prix_absent: mode !== 'per_time' && !isProvided(sc.unit_price),
        nature: 'interne',
        supplier_id: null,
        mode,
        taux_type: sc.taux_type ?? null,
        temps_min: num(sc.temps_min),
        minutes_total: round3(num(sc.temps_min) * qty),
        // Pas de coefficient d'achat ni de marge d'achat : c'est du
        // travail de la maison, la marge commerciale s'applique dessus
        // comme sur le reste.
        unit_price: mode === 'per_time' ? null : num(sc.unit_price),
        base_cost: base === null ? round2(cost) : round2(base),
        cost_total: round2(cost),
      };
    }

    if (!SUBCONTRACTING_MODES.includes(sc.mode)) {
      warn(`subcontracting[${i}].mode`, 'error', `Mode de sous-traitance inconnu (${sc.label ?? i + 1}).`);
    }
    // ── UN PRIX ABSENT NE BLOQUE PLUS — décision de Charles,
    //    11 août 2026 ──
    //
    // C'était une `error`, donc la pièce était incomplète, donc le PDF
    // et l'envoi étaient refusés. La règle écrite disait « un zingage
    // non chiffré ne part jamais à 0 € » ; elle reste vraie, et c'est
    // ce qui rend ce changement acceptable :
    //
    //   NE PAS BLOQUER N'EST PAS COMPTER ZÉRO.
    //
    // La ligne existe, elle est écartée des totaux, elle s'affiche
    // « à confirmer », et elle est COMPTÉE — elle n'entre nulle part en
    // se faisant passer pour gratuite.
    if (!isProvided(sc.unit_price)) {
      warn(`subcontracting[${i}].unit_price`, 'warning',
        `Prix de sous-traitance à confirmer (${sc.label ?? i + 1}) : non compté dans le total.`);
    }
    if (sc.mode === 'per_kg' && !(massTotalKg > 0)) {
      warn(`subcontracting[${i}].mode`, 'error', `Sous-traitance au kilo sans masse calculable (${sc.label ?? i + 1}).`);
    }

    const unitPrice = num(sc.unit_price);
    let base;
    switch (sc.mode) {
      case 'flat':
        base = unitPrice;
        break;
      case 'per_piece':
        base = unitPrice * qty;
        break;
      case 'per_kg':
        base = unitPrice * massTotalKg;
        break;
      default:
        base = 0;
    }
    const afterMin = Math.max(base, num(sc.min_invoice));
    // Le défaut d'une ligne de sous-traitance est le coefficient de
    // SOUS-TRAITANCE des paramètres, et non celui d'ACHAT du composant
    // du commerce : ce sont deux notions différentes, et les confondre
    // faisait dépendre le prix d'un traitement d'un réglage qui décrit
    // l'approvisionnement d'une pièce achetée.
    const coefRaw = isProvided(sc.purchase_coef)
      ? sc.purchase_coef
      : (isProvided(defaults.coefficient_sous_traitance)
        ? defaults.coefficient_sous_traitance
        : purchaseCoefRaw);
    if (!isProvided(coefRaw)) {
      warn(`subcontracting[${i}].purchase_coef`, 'warning', `Coefficient d'achat absent (${sc.label ?? i + 1}) : 1 appliqué.`);
    }
    const coef = isProvided(coefRaw) ? num(coefRaw) : 1;

    return {
      label: sc.label ?? null,
      treatment_id: sc.treatment_id ?? null,
      prix_absent: !isProvided(sc.unit_price),
      nature: 'sous_traite',
      supplier_id: sc.supplier_id ?? null,
      mode: sc.mode ?? null,
      unit_price: unitPrice,
      min_invoice: num(sc.min_invoice),
      purchase_coef: coef,
      base_cost: round2(base),
      cost_total: round2(afterMin * coef),
    };
  });
  // Les deux natures sont totalisées SÉPARÉMENT : l'une est du temps
  // d'atelier, l'autre un achat qui porte la marge. Les mélanger dans un
  // seul chiffre rendrait le devis illisible et l'écart au réel
  // inanalysable.
  // Les lignes SANS prix sont écartées des totaux — et comptées. Les y
  // laisser à 0 les ferait passer pour gratuites ; les écarter en
  // silence rendrait le total faux sans que rien ne le dise.
  const chiffrable = (sc) => !sc.prix_absent;
  const internalCost = subcontracting
    .filter((sc) => sc.nature === 'interne' && chiffrable(sc))
    .reduce((s, sc) => s + sc.cost_total, 0);
  const subcontractingCost = subcontracting
    .filter((sc) => sc.nature !== 'interne' && chiffrable(sc))
    .reduce((s, sc) => s + sc.cost_total, 0);
  const traitementsSansPrix = subcontracting.filter((sc) => sc.prix_absent).length;

  // ── Composant du commerce ──
  // Le coût d'achat entre dans le coût de revient et la marge s'applique
  // dessus comme sur le reste (CLAUDE.md §6, « Synthèse »). Le coefficient
  // d'achat et la marge commerciale sont deux choses distinctes : le premier
  // couvre l'approvisionnement, la seconde est la décision commerciale.
  const purchase = { cost_total: 0 };
  if (isPurchase) {
    if (!isProvided(piece.purchase_price) || num(piece.purchase_price) <= 0) {
      warn('purchase_price', 'error', "Prix d'achat absent sur un composant du commerce.");
    }
    if (!isProvided(purchaseCoefRaw)) {
      warn('purchase_coef', 'warning', "Coefficient d'achat absent : 1 appliqué.");
    }
    const coef = isProvided(purchaseCoefRaw) ? num(purchaseCoefRaw) : 1;
    purchase.unit_price = num(piece.purchase_price);
    purchase.purchase_coef = coef;
    purchase.cost_total = purchase.unit_price * qty * coef;
  }

  // ── Synthèse ──
  const costTotal =
    num(material.cost_total) + workCost + internalCost + subcontractingCost + purchase.cost_total;
  const costUnit = costTotal / qty;

  // ══════════════════════════════════════════════════════════════
  // ON N'APPELLE JAMAIS « PRIX » CE QUI N'EN EST PAS UN.
  // ══════════════════════════════════════════════════════════════
  //
  // Avec des taux à 0 €/h, l'écran affichait « Prix × 1 — 1,00 € » en
  // gras : la matière à 0,98 € plus 0,02 € d'arrondi COMMERCIAL. Le
  // 0,98 n'était pas en cause — il est juste, et il reste affiché. Ce
  // qui trompait, c'est la ligne du bas : un coût partiel nommé
  // « prix », avec un arrondi de vente posé dessus.
  //
  // La marge, le minimum de facturation et l'arrondi commercial sont
  // des opérations de VENTE. Elles n'ont de sens que sur un coût
  // COMPLET : appliquées à un coût amputé, elles fabriquent un nombre
  // qui a toutes les apparences d'un prix et n'en est pas un.
  //
  // Un prix indisponible est une information ; un faux prix est un
  // piège — et c'est celui-là qui part chez un client.
  const complet = !warnings.some((w) => w.level === 'error');

  // L'USINAGE est déjà un prix de vente : aucune marge par-dessus.
  // Le minimum de facturation et l'arrondi commercial s'appliquent au
  // prix unitaire de l'usinage SEULEMENT — les lignes matière,
  // traitement et transport sortent au montant retenu.
  // ── Le COEFFICIENT DE SÉCURITÉ — décision du 10 août 2026 ──
  //
  // Ce n'est pas le retour de la marge globale supprimée le 5 août. La
  // marge était un réglage GLOBAL qui devait rester à 1 — donc un piège.
  // Celui-ci vit sur la PIÈCE, vaut 1 tant que personne ne l'écrit, et
  // s'affiche sur sa propre ligne dans la synthèse avec le montant qu'il
  // ajoute. Un client difficile, une pièce dont on n'est pas sûr : c'est
  // une décision par affaire, prise en connaissance de cause.
  //
  // Il porte sur l'USINAGE seulement — ce que Mertens engage. La matière
  // et la sous-traitance sont de la revente à l'euro près ; les majorer
  // au nom de la « sécurité » reviendrait à gonfler un prix qu'on
  // annonce comme non engagé.
  const coefSecurite = isProvided(piece.coefficient_securite)
    ? num(piece.coefficient_securite, 1)
    : 1;
  const usinageAvantCoef = workCost + internalCost;
  const usinageLot = round2(usinageAvantCoef * coefSecurite);
  const priceLot = complet ? Math.max(usinageLot, minInvoice) : null;
  const priceUnit = complet ? roundCommercial(priceLot / qty, rounding) : null;
  // Règle ferme : le total se DÉDUIT du prix unitaire. Jamais l'inverse.
  const priceTotal = complet ? round2(priceUnit * qty) : null;

  // ── Les trois lignes HORS ENGAGEMENT ──
  //
  // Mertens n'est responsable ni du prix ni de la disponibilité de la
  // matière : ça bouge sur le marché, et sur une commande le prix peut
  // changer six mois plus tard. Elles sortent en lignes SÉPARÉES,
  // jamais fondues dans le prix pièce.
  // Un montant retenu à la main part TEL QUEL sur l'offre : « c'est lui
  // qui part, quelle que soit la somme des coûts matière calculés »
  // (§1.1). Lui appliquer le coefficient de revente reviendrait à
  // majorer un nombre que quelqu'un a déjà arrêté — donc à ne pas le
  // retenir.
  const ligneMatiere = material.fournie_par_client
    ? 0
    : material.montant_retenu != null
      ? round2(material.montant_retenu)
      : material.cout_inconnu || isPurchase
        ? null
        : round2(num(material.cost_total) * coefMatiere);
  // ── LE COEFFICIENT NE PASSE QU'UNE FOIS ──
  //
  // `subcontractingCost` est déjà la somme des lignes, et chaque ligne
  // porte DÉJÀ son coefficient (`cost_total = afterMin × coef`). La
  // remultiplier par `coefSousTraitance` l'appliquait une seconde fois :
  // 100 € de zingage sortaient à 138 € au lieu de 120 €.
  //
  // Idem pour le composant du commerce : `purchase.cost_total` porte
  // déjà son `purchase_coef`.
  const ligneTraitement = round2(subcontractingCost + num(purchase.cost_total));
  // Le transport est VIDE par défaut (§4) : il n'apparaît que s'il est
  // rempli. `round2(num(null))` affichait « Transport 0,00 € » sur
  // toutes les pièces du dépôt — un zéro qui affirme « c'est gratuit »
  // là où personne n'a rien décidé. Il compte pour zéro dans le total,
  // ce qui est vrai, et s'affiche « — », ce qui est honnête.
  const ligneTransport = isProvided(piece.transport_matiere)
    ? round2(num(piece.transport_matiere) * coefTransport)
    : null;
  // Un total qui additionne une ligne INCONNUE serait faux dans le sens
  // le plus dangereux : trop bas, et d'apparence complète. Tant que la
  // matière n'a pas de prix, il n'y a pas de total hors engagement — il
  // y a un tiret, et la raison à côté.
  const totalHorsEngagement =
    ligneMatiere === null ? null : round2(ligneMatiere + ligneTraitement + num(ligneTransport));

  const detail = {
    qty,
    is_purchase: isPurchase,
    material,
    work,
    work_cost: round2(workCost),
    subcontracting,
    internal_cost: round2(internalCost),
    subcontracting_cost: round2(subcontractingCost),
    purchase,
    cost_total: round2(costTotal),
    cost_unit: round2(costUnit),
    // ── Les quatre blocs de l'offre, séparés ──
    usinage_lot: round2(usinageLot),
    // Ce que le coefficient a ajouté, en clair. Un coefficient qui ne
    // montre pas son effet est exactement le réglage qu'on a supprimé.
    usinage_avant_coefficient: round2(usinageAvantCoef),
    coefficient_securite: coefSecurite,
    coefficient_securite_montant:
      coefSecurite === 1 ? null : round2(usinageLot - usinageAvantCoef),
    usinage_unitaire: complet ? round2(usinageLot / qty) : null,
    ligne_matiere: ligneMatiere,
    ligne_traitement: ligneTraitement,
    // Combien de lignes ne sont PAS dans ce montant. L'écran et le PDF
    // le disent : « + 2 traitements à confirmer ».
    traitements_sans_prix: traitementsSansPrix,
    ligne_transport: ligneTransport,
    total_hors_engagement: totalHorsEngagement,
    coefficient_matiere: coefMatiere,
    coefficient_sous_traitance: coefSousTraitance,
    // D'OÙ vient chaque coefficient. Sans ça, l'écran ne peut pas dire
    // « le vôtre » ou « celui des paramètres », et le deviseur ne sait
    // pas s'il regarde une décision ou un défaut.
    coefficient_matiere_source: coefMatiereSource,
    coefficient_traitement_source: coefTraitementSource,
    coefficient_transport: coefTransport,
    min_invoice: minInvoice,
    min_invoice_applied: complet ? priceLot > usinageLot : false,
    rounding_rule: rounding,
    price_lot_before_rounding: complet ? round2(priceLot) : null,
    rounding_amount: complet ? round2(priceTotal - priceLot) : null,
    price_unit: priceUnit,
    price_total: priceTotal,
    /**
     * Ce qu'on affiche À LA PLACE du prix quand il n'y en a pas.
     *
     * Le coût partiel RESTE visible — il est juste, et le cacher ferait
     * croire que le programme n'a rien compris alors qu'il a tout
     * compris sauf une entrée. Ce qui disparaît, c'est le mot « prix »
     * et tout ce qui relève de la vente.
     */
    cout_partiel: complet ? null : round2(costTotal),
    prix_indisponible: complet
      ? null
      : warnings.filter((w) => w.level === 'error').map((w) => w.message).join(' '),
  };

  return {
    price_unit: priceUnit,
    price_total: priceTotal,
    cost_unit: round2(costUnit),
    cost_total: round2(costTotal),
    warnings,
    complete: complet,
    detail,
    // Snapshot complet et lisible : c'est lui qui permettra plus tard
    // d'expliquer un prix poste par poste sans qu'un modèle l'invente,
    // et qui garantit qu'un devis rouvert après la bascule affiche le
    // même prix au centime.
    snapshot: {
      formula_version: PRICING_FORMULA_VERSION,
      computed_at: options.computedAt ?? new Date().toISOString(),
      inputs: {
        qty,
        is_purchase: isPurchase,
        // Plus de marge : le taux EST le prix de vente. Les trois
        // coefficients de revente entrent au snapshot à sa place — un
        // devis rejoué dans six mois doit retrouver ceux du jour.
        coefficient_matiere: coefMatiere,
        coefficient_sous_traitance: coefSousTraitance,
        coefficient_transport: coefTransport,
        // Sans lui au snapshot, un devis rouvert dans six mois
        // recalculerait sans le coefficient et afficherait un autre prix.
        coefficient_securite: coefSecurite,
        min_invoice: minInvoice,
        rounding_rule: rounding,
        density: num(piece.density),
        price_per_kg: num(piece.price_per_kg),
        raw_shape: RAW_SHAPES.includes(piece.raw_shape) ? piece.raw_shape : null,
        raw_l: num(piece.raw_l),
        raw_w: num(piece.raw_w),
        raw_h: num(piece.raw_h),
        raw_diameter: num(piece.raw_diameter),
        raw_length: num(piece.raw_length),
        purchase_price: num(piece.purchase_price),
        purchase_coef: isProvided(purchaseCoefRaw) ? num(purchaseCoefRaw) : null,
        // Les trois temps ET les deux taux : c'est ce qui rend le
        // devis rejouable au centime après un changement de tarif.
        cycle_time_min: cycleMin,
        setup_time_min: setupMin,
        programming_time_min: programmingMin,
        machine_rate: machineRate,
        human_rate: humanRate,
        subcontracting: subs.map((s) => ({
          label: s.label ?? null,
          nature: s.nature ?? 'sous_traite',
          temps_min: num(s.temps_min),
          mode: s.mode ?? null,
          unit_price: num(s.unit_price),
          min_invoice: num(s.min_invoice),
          purchase_coef: isProvided(s.purchase_coef) ? num(s.purchase_coef) : null,
        })),
        raw_format: piece.raw_format
          ? {
              kind: piece.raw_format.kind ?? null,
              dimensions: piece.raw_format.dimensions ?? null,
              stock_length: num(piece.raw_format.stock_length),
              kerf_mm: num(piece.raw_format.kerf_mm),
              price_per_kg: isProvided(piece.raw_format.price_per_kg)
                ? num(piece.raw_format.price_per_kg)
                : null,
            }
          : null,
      },
      breakdown: detail,
      results: {
        cost_unit: round2(costUnit),
        cost_total: round2(costTotal),
        price_unit: priceUnit,
        price_total: priceTotal,
      },
      warnings,
    },
  };
}

/**
 * Calcule les paliers de quantité. Le même `computePiece`, en ne changeant
 * que la quantité : le réglage et la programmation s'amortissent tout seuls,
 * et le minimum de facturation comme l'arrondi s'appliquent à chaque palier
 * indépendamment. Un palier ne devient jamais la quantité de référence.
 */
export function computeQuantityTiers(piece, quantities = [], options = {}) {
  return quantities.map((qty) => {
    const r = computePiece(piece, { ...options, qty });
    return {
      qty: Math.max(1, Math.round(num(qty, 1))),
      price_unit: r.price_unit,
      price_total: r.price_total,
      cost_unit: r.cost_unit,
      complete: r.complete,
    };
  });
}


/**
 * LE PRIX D'UNE PIÈCE, TOUT COMPRIS — Philippe, 27 octobre 2026.
 *
 * La vignette donnait l'usinage par pièce (27,67 €), le bas de la bande
 * le total du devis, matière et traitement compris (671,34 €) ; le prix
 * d'UNE pièce tout compris n'était écrit nulle part, et c'est celui que
 * le client compare.
 *
 * Il se calcule PAR PIÈCE — usinage du lot plus sa part indicative,
 * divisés par SA quantité — et jamais comme un total de devis divisé par
 * une quantité : sur un devis à plusieurs références, ce nombre-là ne
 * désigne aucune pièce.
 *
 * `null` quand le prix matière est inconnu : un « tout compris » qui
 * oublierait la matière serait trop bas, et d'apparence complète.
 */
export function prixUnitaireToutCompris(result) {
  const qty = Number(result?.detail?.qty);
  const usinage = result?.price_total;
  const hors = result?.detail?.total_hors_engagement;
  if (usinage == null || hors == null || !(qty > 0)) return null;
  return round2((Number(usinage) + Number(hors)) / qty);
}
/**
 * Total HT d'un devis : somme des prix totaux recalculés.
 * Renvoie aussi la liste des pièces incomplètes — c'est elle qui interdit
 * la génération du PDF et l'envoi.
 */
export function computeDevisTotal(pieces = [], options = {}) {
  let usinage = 0;
  let horsEngagement = 0;
  let sansPrixMatiere = 0;
  const incomplete = [];
  pieces.forEach((piece, index) => {
    const r = computePiece(piece, options);
    usinage += num(r.price_total);
    // Une seule pièce dont le prix matière est inconnu rend la SOMME
    // hors engagement inconnue : additionner les autres et la présenter
    // comme le total donnerait un chiffre trop bas, d'apparence
    // complète. Même règle qu'au §1.3 pour l'encart.
    //
    // Mais elle ne rend pas l'USINAGE inconnu, et c'est la part que
    // Mertens engage. La cacher pour une nuance non rattachée sur une
    // pièce parmi vingt-deux serait le défaut inverse — un écran qui
    // n'affiche plus rien alors qu'il sait presque tout.
    if (r.detail?.total_hors_engagement == null) sansPrixMatiere += 1;
    else horsEngagement += num(r.detail.total_hors_engagement);
    if (!r.complete) {
      // `price_total` voyage avec l'incomplétude : c'est lui qui décide
      // si le devis peut sortir. Une pièce peut porter un avertissement
      // bloquant ET un prix juste — un mode de sous-traitance inconnu, par
      // exemple, n'empêche pas l'usinage d'être chiffré.
      incomplete.push({
        index,
        id: piece.id ?? null,
        name: piece.name ?? null,
        warnings: r.warnings,
        price_total: r.price_total ?? null,
      });
    }
  });
  const inconnu = sansPrixMatiere > 0;
  return {
    // ── TROIS nombres, parce qu'il y en a trois ──
    //
    // « Total du devis » affichait la somme des prix d'USINAGE et
    // s'appelait quand même « total », pendant que la synthèse de
    // chaque pièce affichait un TOTAL HT qui, lui, comptait les quatre
    // blocs. Deux totaux différents pour le même devis, sur le même
    // écran, sans que rien ne dise lequel était lequel.
    //
    // Ce que Mertens ENGAGE, ce qui est INDICATIF, et leur somme. Le
    // premier n'est pas « le total », c'est la part qu'on garantit.
    total_usinage: round2(usinage),
    total_hors_engagement: inconnu ? null : round2(horsEngagement),
    total_ht: inconnu ? null : round2(usinage + horsEngagement),
    // Ce qui a QUAND MÊME pu être calculé reste visible : il est juste,
    // et le cacher ferait croire que le programme n'a rien compris alors
    // qu'il a tout compris sauf une nuance. Même mécanique que
    // `matiere_partielle` dans `encartMatiere`.
    hors_engagement_partiel: inconnu ? round2(horsEngagement) : null,
    pieces_sans_prix_matiere: sansPrixMatiere,
    incomplete,
  };
}

/**
 * L'ENCART MATIÈRE ET TRANSPORT du devis — §1.1 et §1.3.
 *
 * ── Pourquoi ça n'est pas dans `total_ht` ──
 *
 * `total_ht` est la somme des USINAGES, et l'usinage est le seul poste
 * que Mertens engage. La matière et le transport sortent en lignes
 * séparées parce que leur prix n'est pas tenu : il bouge sur le marché,
 * et sur une commande passée six mois plus tard il aura changé. C'est la
 * demande n°1 du RDV du 5 août, répétée cinq fois.
 *
 * ── L'ordre du §1.3, appliqué tel quel ──
 *
 *   1. montant matière saisi sur le DEVIS
 *   2. somme des coûts matière des pièces
 *
 * Et à chaque niveau, la valeur remplacée reste rendue : l'écran affiche
 * les deux, « retenu 60,00 € — calculé 47,80 € », jamais le seul
 * gagnant. Un montant retenu en silence est un montant que personne ne
 * peut contrôler.
 *
 * @param {object} devis  l'en-tête, avec `montant_matiere_retenu` et
 *                        `transport_matiere`
 * @param {Array<{detail?: object}>} resultats  les pièces déjà calculées
 */
export function encartMatiere(devis = {}, resultats = []) {
  // Une seule pièce sans prix matière rend la SOMME inconnue : additionner
  // les autres et présenter le résultat comme le total matière du devis
  // serait un chiffre trop bas, d'apparence complète. On le dit.
  const lignes = resultats.map((r) => r?.detail?.ligne_matiere ?? null);
  const inconnues = lignes.filter((v) => v === null).length;
  const calcule = round2(lignes.reduce((s, v) => s + num(v), 0));
  const retenu = isProvided(devis.montant_matiere_retenu)
    ? num(devis.montant_matiere_retenu)
    : null;

  // Le transport n'a AUCUNE valeur calculée : vide, il n'existe pas et
  // n'apparaît pas sur l'offre. C'est écrit au §1.1, et c'est la
  // différence entre « zéro euro de transport » — une affirmation — et
  // « pas de ligne transport » — une absence.
  // ── Le transport existe maintenant AUX DEUX niveaux ──
  //
  // Depuis le 10 août il vit aussi sur la pièce. Additionner le montant
  // du devis ET celui des pièces les compterait deux fois ; ignorer
  // celui des pièces le perdrait. C'est la même règle que pour la
  // matière (§1.3) : **ce qui est saisi au niveau du DEVIS prévaut, et
  // à défaut on somme les pièces.**
  const transportPieces = resultats.reduce((s2, r) => s2 + num(r?.detail?.ligne_transport), 0);
  const transport = isProvided(devis.transport_matiere)
    ? num(devis.transport_matiere)
    : round2(transportPieces);

  const traitement = round2(
    resultats.reduce((s, r) => s + num(r?.detail?.ligne_traitement), 0),
  );

  return {
    matiere_calculee: inconnues ? null : calcule,
    // Ce qui a QUAND MÊME pu être calculé reste visible : il est juste,
    // et le cacher ferait croire que le programme n'a rien compris alors
    // qu'il a tout compris sauf une nuance.
    matiere_partielle: inconnues ? calcule : null,
    pieces_sans_prix_matiere: inconnues,
    matiere_retenue: retenu,
    // Ce qui part réellement sur l'offre.
    matiere: retenu ?? (inconnues ? null : calcule),
    matiere_remplacee: retenu != null && retenu !== calcule ? calcule : null,
    traitement,
    transport,
    // Ce que le montant du devis REMPLACE, quand il en remplace un.
    transport_remplace:
      isProvided(devis.transport_matiere) && round2(transportPieces) !== num(devis.transport_matiere)
        ? round2(transportPieces)
        : null,
    fournisseur: devis.matiere_fournisseur ?? null,
    offre_ref: devis.matiere_offre_ref ?? null,
    offre_date: devis.matiere_offre_date ?? null,
    total:
      retenu == null && inconnues
        ? null
        : round2((retenu ?? calcule) + traitement + num(transport)),
  };
}
